import { defineMessages, MessageDescriptor } from 'react-intl';
import type { KitShape, StudioMoment } from 'fym-shared/src/music24/studioThemes';

/** The copy of the Studio (design release-kit l1–l5, n1–n7). */
export const studioMessages = defineMessages({
  titleSong: { id: 'app.sml.promote.studio.titleSong', defaultMessage: 'Make something for {title}' },
  pickTitle: { id: 'app.sml.promote.studio.pick.title', defaultMessage: 'Pick a song' },
  songMasterReady: { id: 'app.sml.promote.studio.song.master', defaultMessage: 'Master ✓' },
  songNoMaster: { id: 'app.sml.promote.studio.song.noMaster', defaultMessage: 'No master' },
  songLyrics: { id: 'app.sml.promote.studio.song.lyrics', defaultMessage: 'Lyrics' },
  songOutIn: {
    id: 'app.sml.promote.studio.song.outIn',
    defaultMessage: '{days, plural, one {Out in # day} other {Out in # days}}',
  },
  songOutToday: { id: 'app.sml.promote.studio.song.outToday', defaultMessage: 'Out today' },
  changeSong: { id: 'app.sml.promote.studio.song.change', defaultMessage: 'Change song' },
  // The steps of design t2: the asset types, then the details.
  stepsLabel: { id: 'app.sml.promote.studio.steps.label', defaultMessage: 'Steps' },
  stepAsset: { id: 'app.sml.promote.studio.step.assetTypes', defaultMessage: 'Asset types' },
  stepAssetFirst: { id: 'app.sml.promote.studio.step.assetFirst', defaultMessage: '1 · Asset' },
  stepDetailsSecond: { id: 'app.sml.promote.studio.step.detailsSecond', defaultMessage: '2 · Details' },
  stepDone: { id: 'app.sml.promote.studio.step.done', defaultMessage: '{step} · {name} ✓' },
  back: { id: 'app.sml.promote.studio.back', defaultMessage: 'Back' },
  sectionSocial: { id: 'app.sml.promote.studio.section.social', defaultMessage: 'Social reels' },
  sectionLoops: { id: 'app.sml.promote.studio.section.loops', defaultMessage: 'Loops for the services' },
  needsMaster: { id: 'app.sml.promote.studio.needsMaster', defaultMessage: 'Needs the master' },
  needsReleaseDate: { id: 'app.sml.promote.studio.needsReleaseDate', defaultMessage: 'Needs a release date' },
  credits: {
    id: 'app.sml.promote.studio.credits',
    defaultMessage: '{count, plural, one {# credit} other {# credits}}',
  },
  momentTitle: { id: 'app.sml.promote.studio.moment.title', defaultMessage: 'Moment' },
  momentHint: { id: 'app.sml.promote.studio.moment.hint', defaultMessage: 'What it says' },
  noLyrics: { id: 'app.sml.promote.studio.moment.noLyrics', defaultMessage: 'Add lyrics in the catalog' },
  shapeTitle: { id: 'app.sml.promote.studio.shape.title', defaultMessage: 'Shape' },
  shapeHint: { id: 'app.sml.promote.studio.shape.hint', defaultMessage: 'Where you post it' },
  shapeNote: {
    id: 'app.sml.promote.studio.shape.note',
    defaultMessage: '9:16 for reels, TikTok, Shorts and stories. 1:1 and 4:5 for the feed.',
  },
  lengthTitle: { id: 'app.sml.promote.studio.length.title', defaultMessage: 'Length' },
  lengthHint: { id: 'app.sml.promote.studio.length.hint', defaultMessage: 'Starts at the hook, with the song' },
  seconds: { id: 'app.sml.promote.studio.seconds', defaultMessage: '{count} s' },
  windowTitle: { id: 'app.sml.promote.studio.window.title', defaultMessage: 'Which part of the song' },
  windowHint: {
    id: 'app.sml.promote.studio.window.hint',
    defaultMessage: 'The Canvas plays with no sound. The part sets the motion.',
  },
  windowLabel: { id: 'app.sml.promote.studio.window.label', defaultMessage: 'Start of the part' },
  windowNote: {
    id: 'app.sml.promote.studio.window.note',
    defaultMessage: 'Starts at the hook. Drag the window to change it.',
  },
  windowHook: { id: 'app.sml.promote.studio.window.hook', defaultMessage: 'Hook {time}' },
  specTitle: { id: 'app.sml.promote.studio.spec.title', defaultMessage: 'Spec check' },
  specHint: { id: 'app.sml.promote.studio.spec.hint', defaultMessage: 'The rules of the streaming service' },
  specSize: { id: 'app.sml.promote.studio.spec.size', defaultMessage: '9:16, 1080 × 1920, MP4' },
  specLoopBars: {
    id: 'app.sml.promote.studio.spec.loopBars',
    defaultMessage: 'Four bars of the song (3 to 8 seconds), loops with no jump',
  },
  specText: { id: 'app.sml.promote.studio.spec.text', defaultMessage: 'No text, no logos, no links' },
  specBottom: {
    id: 'app.sml.promote.studio.spec.bottom',
    defaultMessage: 'Nothing important in the bottom third, where the player sits',
  },
  countdownTitle: {
    id: 'app.sml.promote.studio.countdown.title',
    defaultMessage: 'Three videos: 7, 3 and 1 days before the release',
  },
  countdownBody: {
    id: 'app.sml.promote.studio.countdown.body',
    defaultMessage: "9:16 with the song, for stories. They go on the Plan tab's countdown days.",
  },
  countdownFewer: {
    id: 'app.sml.promote.studio.countdown.fewer',
    defaultMessage:
      'The release is less than 7 days away, so only the days still to come are made. {count, plural, one {# video} other {# videos}}.',
  },
  instructionsTitle: { id: 'app.sml.promote.studio.instructions.title', defaultMessage: 'Instructions' },
  instructionsHint: {
    id: 'app.sml.promote.studio.instructions.hint',
    defaultMessage: 'What MUSIC24 makes. Change it as you like.',
  },
  instructionsNote: {
    id: 'app.sml.promote.studio.instructions.note',
    defaultMessage: "The theme's colours, fonts and texture always apply.",
  },
  instructionsReset: { id: 'app.sml.promote.studio.instructions.reset', defaultMessage: 'Reset to default' },
  instructionsCount: { id: 'app.sml.promote.studio.instructions.count', defaultMessage: '{count} / {max}' },
  railTheme: { id: 'app.sml.promote.studio.rail.theme', defaultMessage: 'Theme' },
  railStyle: { id: 'app.sml.promote.studio.rail.style', defaultMessage: 'Style' },
  themeChipLabel: { id: 'app.sml.promote.studio.themeChip.label', defaultMessage: 'Theme' },
  themeChipChange: { id: 'app.sml.promote.studio.themeChip.change', defaultMessage: 'Change' },
  themeChipAria: { id: 'app.sml.promote.studio.themeChip.aria', defaultMessage: 'Change the theme of {title}' },
  styleTitle: { id: 'app.sml.promote.studio.style.title', defaultMessage: 'Style' },
  styleHintImage: {
    id: 'app.sml.promote.studio.style.hintImage',
    defaultMessage: 'Where the cover goes. The colours, the type and the texture come from the theme {theme}.',
  },
  styleHintLoop: {
    id: 'app.sml.promote.studio.style.hintLoop',
    defaultMessage: 'What moves. The colours, the type and the texture come from the theme {theme}.',
  },
  styleStartsWith: { id: 'app.sml.promote.studio.style.startsWith', defaultMessage: '{theme} starts with {style}.' },
  styleAiBadge: { id: 'app.sml.promote.studio.style.aiBadge', defaultMessage: 'AI' },
  styleShape: { id: 'app.sml.promote.studio.style.shape', defaultMessage: 'Shape' },
  countdownDays: {
    id: 'app.sml.promote.studio.asset.countdownDays',
    defaultMessage: '{count, plural, one {# day} other {# days}}',
  },
  makeIt: { id: 'app.sml.promote.studio.makeIt', defaultMessage: 'Make it' },
  makeItCredits: {
    id: 'app.sml.promote.studio.makeItCredits',
    defaultMessage: 'Make it · {count, plural, one {# credit} other {# credits}}',
  },
  busy: { id: 'app.sml.promote.studio.busy', defaultMessage: 'This is already being made.' },
  // A make action failed for a reason with no M24 code (a validation 400, a 5xx, no network).
  actionFailed: {
    id: 'app.sml.promote.studio.actionFailed',
    defaultMessage: 'We could not start this. Try again; if it happens again, contact us.',
  },
  // Why "Make it" is off: the rail says it, so a grey button never reads as "no credits".
  blockedPickLines: { id: 'app.sml.promote.studio.blocked.pickLines', defaultMessage: 'Pick the lines first.' },
  blockedFields: { id: 'app.sml.promote.studio.blocked.fields', defaultMessage: 'Fill in the choices above first.' },
  blockedSavingTheme: {
    id: 'app.sml.promote.studio.blocked.savingTheme',
    defaultMessage: 'Saving the theme of the song. Wait a moment.',
  },
  resultMaking: { id: 'app.sml.promote.studio.result.making', defaultMessage: 'Making the {name}' },
  resultReady: { id: 'app.sml.promote.studio.result.ready', defaultMessage: 'Your {name} is ready.' },
  resultFailed: { id: 'app.sml.promote.studio.result.failed', defaultMessage: 'MUSIC24 could not make the {name}.' },
  resultSpec: { id: 'app.sml.promote.studio.result.spec', defaultMessage: 'It passed the spec check.' },
  download: { id: 'app.sml.promote.studio.result.download', defaultMessage: 'Download' },
  fileRemoved: {
    id: 'app.sml.promote.studio.result.fileRemoved',
    defaultMessage: 'File removed. We keep the files of the 3 newest revisions. Make a new revision.',
  },
  tryAgain: { id: 'app.sml.promote.studio.result.tryAgain', defaultMessage: 'Try again' },
  seeAll: { id: 'app.sml.promote.studio.result.seeAll', defaultMessage: 'See all assets of {title}' },
  // design: releases-v2 c2.
  makeElse: { id: 'app.sml.promote.studio.result.makeElse', defaultMessage: 'Make something else' },
  failRender: {
    id: 'app.sml.promote.studio.fail.render',
    defaultMessage: 'The render took too long. Try again. No credit was used.',
  },
  failAnalysis: {
    id: 'app.sml.promote.studio.fail.analysis',
    defaultMessage: 'MUSIC24 could not read the song. Try again. No credit was used.',
  },
  failMasterDecode: {
    id: 'app.sml.promote.studio.fail.masterDecode',
    defaultMessage: 'MUSIC24 cannot decode the master. Add it again in the catalog as WAV, FLAC, MP3 or M4A.',
  },
  failMasterTooShort: {
    id: 'app.sml.promote.studio.fail.masterTooShort',
    defaultMessage: 'The master is shorter than 15 s. Add the full song in the catalog.',
  },
  failMasterTooLong: {
    id: 'app.sml.promote.studio.fail.masterTooLong',
    defaultMessage: 'The master is longer than 15 min. Add a shorter file in the catalog.',
  },
  failSpec: {
    id: 'app.sml.promote.studio.fail.spec',
    defaultMessage: 'The file did not pass the spec check. Try again. No credit was used.',
  },
  failPlan: { id: 'app.sml.promote.studio.fail.plan', defaultMessage: 'MUSIC24 could not plan this asset. Try again.' },
  resultStopped: {
    id: 'app.sml.promote.studio.result.stopped',
    defaultMessage: 'We stopped checking on your {name}. It can still be in the making.',
  },
  checkAgain: { id: 'app.sml.promote.studio.result.checkAgain', defaultMessage: 'Check again' },
  failStalled: {
    id: 'app.sml.promote.studio.fail.stalled',
    defaultMessage: 'This took too long. Try again. No credit was used.',
  },
});

/** The moment chips (designs n1, n3). */
export const studioMomentMessages: Record<StudioMoment, MessageDescriptor> = defineMessages({
  preSave: { id: 'app.sml.promote.studio.moment.preSave', defaultMessage: 'Pre-save' },
  outNow: { id: 'app.sml.promote.studio.moment.outNow', defaultMessage: 'Out now' },
  lyricLine: { id: 'app.sml.promote.studio.moment.lyricLine', defaultMessage: 'A lyric line' },
  announce: { id: 'app.sml.promote.studio.moment.announce', defaultMessage: 'Announce' },
});

/** The shapes a theme offers (design n3). */
export const STUDIO_SHAPES: ReadonlyArray<KitShape> = ['9:16', '1:1', '4:5'];
